import { supabase } from "@/lib/supabaseClient";

export type ReviewWord = {
  id: string;
  word_id: string;
  review_stage: number;
  review_interval_days: number;
  word: string;
  meaning: string;
  set_id: string;
};

export const REVIEW_QUALITIES = ["again", "hard", "normal", "easy"] as const;

export type ReviewQuality = (typeof REVIEW_QUALITIES)[number];

export type ReviewSchedule = {
  quality: ReviewQuality;
  interval_days: number;
  review_stage: number;
  next_review_at: string;
  is_graduated: boolean;
};

export type ReviewSchedulePreview = Pick<ReviewSchedule, "quality" | "interval_days" | "review_stage" | "is_graduated">;

const INTERVAL_DAYS_BY_STAGE = [1, 1, 3, 7, 14, 30, 0] as const;
const STAGE_ADVANCE_BY_QUALITY: Record<ReviewQuality, number> = {
  again: 0,
  hard: 0,
  normal: 1,
  easy: 2,
};

export function getReviewSchedulePreview(currentStage: number, quality: ReviewQuality): ReviewSchedulePreview {
  const nextStage =
    quality === "again" ? 1 : Math.min(6, Math.max(1, currentStage + STAGE_ADVANCE_BY_QUALITY[quality]));

  return {
    quality,
    interval_days: INTERVAL_DAYS_BY_STAGE[nextStage],
    review_stage: nextStage,
    is_graduated: nextStage >= 6,
  };
}

export type ReviewSummary = {
  reviewCount: number;
  completedTodayCount: number;
  nextReviewAt: string | null;
};

/** 오늘 복습할 단어 조회 (next_review_at <= now, 졸업 아닌 것) */
export async function getReviewWords(limit = 10): Promise<ReviewWord[]> {
  const { data, error } = await supabase
    .from("user_wrong_answers")
    .select("id, word_id, review_stage, review_interval_days, words(word, meaning, set_id)")
    .eq("is_graduated", false)
    .lte("next_review_at", new Date().toISOString())
    .order("next_review_at", { ascending: true })
    .limit(limit);

  if (error) {
    throw error;
  }

  return (data ?? []).map(row => ({
    id: row.id,
    word_id: row.word_id,
    review_stage: row.review_stage,
    review_interval_days: row.review_interval_days,
    word: (row.words as { word: string; meaning: string; set_id: string }).word,
    meaning: (row.words as { word: string; meaning: string; set_id: string }).meaning,
    set_id: (row.words as { word: string; meaning: string; set_id: string }).set_id,
  }));
}

/** 복습 요약 조회 (대시보드 카드용) */
export async function getReviewSummary(): Promise<ReviewSummary> {
  const now = new Date().toISOString();

  // 오늘 복습 가능한 수
  const { count, error: countError } = await supabase
    .from("user_wrong_answers")
    .select("id", { count: "exact", head: true })
    .eq("is_graduated", false)
    .lte("next_review_at", now);

  if (countError) {
    throw countError;
  }

  // 오늘 복습 완료한 수 (last_reviewed_at이 오늘인 행)
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const { count: completedToday, error: completedError } = await supabase
    .from("user_wrong_answers")
    .select("id", { count: "exact", head: true })
    .gte("last_reviewed_at", todayStart.toISOString());

  if (completedError) {
    throw completedError;
  }

  // 다음 예정된 복습 시점 (오늘 이후)
  const { data: nextData, error: nextError } = await supabase
    .from("user_wrong_answers")
    .select("next_review_at")
    .eq("is_graduated", false)
    .gt("next_review_at", now)
    .order("next_review_at", { ascending: true })
    .limit(1);

  if (nextError) {
    throw nextError;
  }

  return {
    reviewCount: count ?? 0,
    completedTodayCount: completedToday ?? 0,
    nextReviewAt: nextData?.[0]?.next_review_at ?? null,
  };
}

/** 기존 boolean 호출 계약. */
export function submitReviewResult(wordId: string, isCorrect: boolean): Promise<void>;
/** 4단계 평가 호출 계약. */
export function submitReviewResult(wordId: string, quality: ReviewQuality): Promise<ReviewSchedule>;
export async function submitReviewResult(
  wordId: string,
  result: boolean | ReviewQuality,
): Promise<void | ReviewSchedule> {
  const params =
    typeof result === "boolean"
      ? { p_word_id: wordId, p_is_correct: result }
      : { p_word_id: wordId, p_quality: result };
  const { data, error } = await supabase.rpc("submit_review_result", params);

  if (error) {
    throw error;
  }

  if (typeof result === "boolean") {
    return;
  }

  const schedule = Array.isArray(data) ? data[0] : data;

  if (!schedule) {
    throw new Error("복습 일정이 저장되지 않았습니다.");
  }

  return { ...schedule, quality: schedule.quality as ReviewQuality };
}

/** 보기 생성용: 같은 세트에서 랜덤 meaning 가져오기 */
export async function getRandomMeanings(setId: string, excludeMeaning: string, count = 3): Promise<string[]> {
  const { data, error } = await supabase
    .from("words")
    .select("meaning")
    .eq("set_id", setId)
    .neq("meaning", excludeMeaning)
    .limit(count * 3); // 넉넉하게 가져와서 셔플

  if (error) {
    throw error;
  }

  const meanings = [...new Set((data ?? []).map(d => d.meaning))];

  // 피셔-예이츠 셔플
  for (let i = meanings.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [meanings[i], meanings[j]] = [meanings[j], meanings[i]];
  }

  return meanings.slice(0, count);
}
